import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { Text, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSMobileInput, { DIAL_CODE } from '@/components/ui/PSMobileInput';
import { PSErrorClass } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { useAuthContext } from '@/src/lib/authContext';
import { toast } from '@tamagui/toast/v2';

/**
 * Auth step 1 — enter mobile number.
 *
 * The flow's entry screen: NO headerLeft (nothing to go back to) and no
 * swipe-dismiss (gesture off in the layout) — the flow only moves forward from
 * here. Arriving back here from verify-mobile (via that screen's ← back) shows
 * the number still filled from the draft, which is how "change number" works.
 *
 * Render-only atoms + screen-owned validation (house rule): the "10 digits"
 * judgment lives here (validate on blur, clear on focus); PSMobileInput only
 * restricts input to 10 digits.
 *
 * Submitting sends the SMS. `requestMobileOtp` hides the fact that this is two
 * endpoints (login, falling through to register on a 404) — whether the number is
 * new is the backend's business, and this screen behaves identically either way.
 * We stamp the send in the draft so the next screen's resend countdown starts
 * from the moment the code actually went out, and store the challenge id it must
 * verify against.
 *
 * The draft holds the BARE 10 digits — PSMobileInput shows the `+91` it stands
 * for but doesn't put it in the value, so the request prepends the same
 * `DIAL_CODE` the field displayed.
 */
export default function AuthMobile() {
  const router = useRouter();
  const { mobile, setMobile, markSent } = useAuthContext();
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  // Carried through from a purchase/booking CTA so verify-mobile knows to
  // chain all the way to email instead of stopping at a name — see that
  // screen's docstring.
  const { next, headerCopy } = useLocalSearchParams<{
    next?: string;
    headerCopy?: string;
  }>();

  const isValid = mobile.length === 10;

  const handleSubmit = async () => {
    if (!isValid) {
      setError('Enter a 10-digit mobile number');
      return;
    }

    setSubmitting(true);
    try {
      const challengeId = await authApi.requestMobileOtp(
        `${DIAL_CODE}${mobile}`
      );
      markSent('mobile', mobile, challengeId);
      router.push({
        pathname: '/auth/verify-mobile',
        params: next ? { next, headerCopy } : undefined,
      });
    } catch (err) {
      // A malformed number comes back with `field: "mobile"`, and that field is
      // right here — so it belongs under the input, not in a toast. (We check 10
      // digits ourselves, so this mostly catches rules the server has and we
      // don't.) Everything else — no connection, a 5xx — has no field to sit on.
      if (err instanceof PSErrorClass && err.field === 'mobile') {
        setError(err.message);
      } else {
        toast.error(
          err instanceof PSErrorClass
            ? err.message
            : 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Sign in · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          unstable_headerRightItems: () => [
            {
              type: 'custom',
              element: (
                <PSCircleButton variant="bare" onPress={() => router.back()}>
                  <PSIcon name="close" />
                </PSCircleButton>
              ),
              hidesSharedBackground: true,
            },
          ],
          gestureEnabled: true,
        }}
      />
      <YStack px="$lg" pt="$xl" gap="$lg">
        <Text fontSize="$large" fontWeight="$semibold" color="$textDark">
          Enter your mobile number
        </Text>
        <PSMobileInput
          value={mobile}
          onChangeText={setMobile}
          autoFocus={true}
          error={error}
          onFocus={() => setError(undefined)}
          onBlur={() =>
            setError(isValid ? undefined : 'Enter a 10-digit mobile number')
          }
        />
        <PSButton
          title="Submit"
          loading={submitting}
          loadingTitle="Sending code"
          onPress={handleSubmit}
          disabled={!isValid}
        />
      </YStack>
    </>
  );
}
